import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  ChevronDown,
  PackageCheck,
  FileText,
  CheckCircle,
  XCircle,
  Loader2,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { ConfirmDialog } from '@components/Dialog';
import { DateInputBR } from '@components/DateInputBR';
import { materialReceiptService } from '../../services/materialReceiptService';
import {
  MaterialReceipt,
  MaterialReceiptSearchModel,
  MATERIAL_RECEIPT_STATUS,
  MATERIAL_RECEIPT_STATUS_LABELS,
  MaterialReceiptStatus,
} from '../../types/materialReceipt';

// ─── Helpers ─────────────────────────────────────────────────────

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : ''));
  return d.toLocaleDateString('pt-BR');
};

const StatusBadge: React.FC<{ status: MaterialReceiptStatus }> = ({ status }) => {
  const isConfirmed = status === MATERIAL_RECEIPT_STATUS.CONFIRMED;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        isConfirmed
          ? 'bg-emerald-100 text-emerald-700'
          : 'bg-amber-100 text-amber-700'
      }`}
    >
      {MATERIAL_RECEIPT_STATUS_LABELS[status] ?? String(status)}
    </span>
  );
};


// ─── Component ───────────────────────────────────────────────────

export const MaterialReceiptListView: React.FC = () => {
  const navigate = useNavigate();

  const [receipts, setReceipts] = useState<MaterialReceipt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Filtros
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    provider_id: undefined as number | undefined,
    date_from: '',
    date_to: '',
    status: undefined as MaterialReceiptStatus | undefined,
  });

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReceipts, setTotalReceipts] = useState(0);
  const pageSize = 20;

  // Stats
  const [stats, setStats] = useState({ total: 0, withDamage: 0, withPO: 0 });

  // Delete
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Confirm
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [confirmingRow, setConfirmingRow] = useState(false);

  // ── Load ──────────────────────────────────────────────────────

  const loadReceipts = async () => {
    try {
      setLoading(true);
      setError(null);

      const searchFilters: MaterialReceiptSearchModel = {
        page: currentPage,
        pageSize,
        searchName: search.trim() || undefined,
        provider_id: filters.provider_id,
        status: filters.status,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
      };

      const response = await materialReceiptService.getMaterialReceipts(searchFilters);
      const data = Array.isArray(response.data) ? response.data : [];
      setReceipts(data);
      setTotalReceipts(response.pagination?.total || data.length);
      setTotalPages(response.pagination?.totalPages || 1);

      setStats({
        total: data.length,
        withDamage: data.filter(r => r.items?.some(i => i.damaged)).length,
        withPO: data.filter(r => r.items?.some(i => i.purchase_order_id)).length,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar recebimentos');
    } finally {
      setLoading(false);
    }
  };

  // Este efeito já roda na montagem — não precisa de um segundo com [] (eram duas
  // requisições em toda abertura da tela).
  useEffect(() => { loadReceipts(); }, [currentPage, filters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) setCurrentPage(1);
      else loadReceipts();
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Filter dropdown outside-click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.filter-dropdown') && !target.closest('[data-filter-trigger]')) {
        setShowFilters(false);
      }
    };
    if (showFilters) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilters]);

  // ── Delete ────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (deleteId === null) return;
    try {
      setDeleting(true);
      await materialReceiptService.deleteMaterialReceipt(deleteId);
      setDeleteId(null);
      loadReceipts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir recebimento');
    } finally {
      setDeleting(false);
    }
  };

  // ── Confirm ───────────────────────────────────────────────────

  const handleConfirmRow = async () => {
    if (confirmId === null) return;
    try {
      setConfirmingRow(true);
      await materialReceiptService.updateMaterialReceiptStatus(confirmId, {
        status: MATERIAL_RECEIPT_STATUS.CONFIRMED,
      });
      setConfirmId(null);
      loadReceipts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao confirmar recebimento');
    } finally {
      setConfirmingRow(false);
    }
  };

  // ── Active filter count ───────────────────────────────────────

  const activeFilterCount = [
    filters.provider_id !== undefined,
    !!filters.date_from,
    !!filters.date_to,
    filters.status !== undefined,
  ].filter(Boolean).length;

  // ── Render ────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Recebimento de Materiais</h1>
          <p className="text-sm text-slate-500 mt-1">
            {totalReceipts} recebimento{totalReceipts !== 1 ? 's' : ''} encontrado{totalReceipts !== 1 ? 's' : ''}
          </p>
        </div>
        <PermissionGuard permission="material_receipts.create">
          <Button icon={<Plus size={20} />} onClick={() => navigate('/material-receipts/new')}>
            Novo Recebimento
          </Button>
        </PermissionGuard>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="py-3 px-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileText size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Total</p>
              <p className="text-lg font-bold text-slate-900">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3 px-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <XCircle size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Com Avaria</p>
              <p className="text-lg font-bold text-slate-900">{stats.withDamage}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3 px-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CheckCircle size={20} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Com Pedido</p>
              <p className="text-lg font-bold text-slate-900">{stats.withPO}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & filters */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                className="input pl-10 w-full"
                placeholder="Buscar por nº recebimento, fornecedor ou cliente..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="relative">
              <Button
                variant="outline"
                data-filter-trigger
                icon={<Filter size={18} />}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filtros
                {activeFilterCount > 0 && (
                  <span className="ml-1.5 bg-emerald-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
                <ChevronDown size={16} className="ml-1" />
              </Button>

              {showFilters && (
                <div className="filter-dropdown absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-lg border border-slate-200 p-4 z-30 space-y-4">
                  {/* Datas */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Data de</label>
                      <DateInputBR
                        className="input w-full text-sm"
                        value={filters.date_from}
                        onChange={v => setFilters(prev => ({ ...prev, date_from: v }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Data até</label>
                      <DateInputBR
                        className="input w-full text-sm"
                        value={filters.date_to}
                        onChange={v => setFilters(prev => ({ ...prev, date_to: v }))}
                      />
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                    <select
                      className="input w-full text-sm"
                      value={filters.status !== undefined ? String(filters.status) : ''}
                      onChange={e =>
                        setFilters(prev => ({
                          ...prev,
                          status: e.target.value !== '' ? (Number(e.target.value) as MaterialReceiptStatus) : undefined,
                        }))
                      }
                    >
                      <option value="">Todos</option>
                      <option value={String(MATERIAL_RECEIPT_STATUS.IN_REVIEW)}>
                        {MATERIAL_RECEIPT_STATUS_LABELS[MATERIAL_RECEIPT_STATUS.IN_REVIEW]}
                      </option>
                      <option value={String(MATERIAL_RECEIPT_STATUS.CONFIRMED)}>
                        {MATERIAL_RECEIPT_STATUS_LABELS[MATERIAL_RECEIPT_STATUS.CONFIRMED]}
                      </option>
                    </select>
                  </div>

                  <button
                    className="text-xs text-emerald-600 hover:underline"
                    onClick={() => setFilters({ provider_id: undefined, date_from: '', date_to: '', status: undefined })}
                  >
                    Limpar filtros
                  </button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-emerald-600" size={32} />
            </div>
          ) : receipts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <PackageCheck size={48} className="mb-3" />
              <p className="text-lg font-medium">Nenhum recebimento encontrado</p>
              <p className="text-sm mt-1">Ajuste os filtros ou crie um novo recebimento.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Nº</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase hidden md:table-cell">Data</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Fornecedor / Cliente</th>
                    <th className="py-3 px-4 text-center text-xs font-medium text-slate-500 uppercase md:hidden">Ações</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase hidden md:table-cell">Transportadora</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase hidden md:table-cell">Volumes</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="py-3 px-4 text-center text-xs font-medium text-slate-500 uppercase hidden md:table-cell">Ações</th>
                  </tr>
                </TableHeader>
                <tbody>
                  {receipts.map(receipt => (
                    <TableRow key={receipt.material_receipt_id}>
                      <TableCell className="font-medium text-slate-900">
                        {receipt.material_receipt_id}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{formatDate(receipt.receipt_date)}</TableCell>
                      <TableCell>
                        <div>
                          {receipt.provider_name ? (
                            <>
                              <span className="text-slate-900">{receipt.provider_name}</span>
                              {receipt.provider_document && (
                                <span className="block text-xs text-slate-400">{receipt.provider_document}</span>
                              )}
                            </>
                          ) : receipt.customer_name ? (
                            <>
                              <span className="text-slate-900">{receipt.customer_name}</span>
                              <span className="block text-xs text-emerald-600">Cliente</span>
                            </>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </div>
                      </TableCell>
                      {/* Ações mobile — logo após Fornecedor/Cliente */}
                      <TableCell className="text-center md:hidden">
                        <div className="flex items-center justify-center gap-1">
                          <PermissionGuard permission="material_receipts.read">
                            <button
                              className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                              title="Visualizar"
                              onClick={() => navigate(`/material-receipts/${receipt.material_receipt_id}`)}
                            >
                              <Eye size={16} />
                            </button>
                          </PermissionGuard>
                          {receipt.status !== MATERIAL_RECEIPT_STATUS.CONFIRMED && (
                            <PermissionGuard permission="material_receipts.write">
                              <button
                                className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                                title="Confirmar Recebimento"
                                onClick={() => setConfirmId(receipt.material_receipt_id)}
                              >
                                <CheckCircle size={16} />
                              </button>
                            </PermissionGuard>
                          )}
                          {receipt.status !== MATERIAL_RECEIPT_STATUS.CONFIRMED && (
                            <PermissionGuard permission="material_receipts.write">
                              <button
                                className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="Editar"
                                onClick={() => navigate(`/material-receipts/${receipt.material_receipt_id}/edit`)}
                              >
                                <Edit size={16} />
                              </button>
                            </PermissionGuard>
                          )}
                          {receipt.status !== MATERIAL_RECEIPT_STATUS.CONFIRMED && (
                            <PermissionGuard permission="material_receipts.delete">
                              <button
                                className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Excluir"
                                onClick={() => setDeleteId(receipt.material_receipt_id)}
                              >
                                <Trash2 size={16} />
                              </button>
                            </PermissionGuard>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 hidden md:table-cell">{receipt.carrier || '—'}</TableCell>
                      <TableCell className="text-slate-600 hidden md:table-cell">{receipt.volumes ?? '—'}</TableCell>
                      <TableCell>
                        <StatusBadge status={receipt.status ?? MATERIAL_RECEIPT_STATUS.IN_REVIEW} />
                      </TableCell>
                      <TableCell className="text-center hidden md:table-cell">
                        <div className="flex items-center justify-center gap-1">
                          <PermissionGuard permission="material_receipts.read">
                            <button
                              className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                              title="Visualizar"
                              onClick={() => navigate(`/material-receipts/${receipt.material_receipt_id}`)}
                            >
                              <Eye size={16} />
                            </button>
                          </PermissionGuard>
                          {receipt.status !== MATERIAL_RECEIPT_STATUS.CONFIRMED && (
                            <PermissionGuard permission="material_receipts.write">
                              <button
                                className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                                title="Confirmar Recebimento"
                                onClick={() => setConfirmId(receipt.material_receipt_id)}
                              >
                                <CheckCircle size={16} />
                              </button>
                            </PermissionGuard>
                          )}
                          {receipt.status !== MATERIAL_RECEIPT_STATUS.CONFIRMED && (
                            <PermissionGuard permission="material_receipts.write">
                              <button
                                className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="Editar"
                                onClick={() => navigate(`/material-receipts/${receipt.material_receipt_id}/edit`)}
                              >
                                <Edit size={16} />
                              </button>
                            </PermissionGuard>
                          )}
                          {receipt.status !== MATERIAL_RECEIPT_STATUS.CONFIRMED && (
                            <PermissionGuard permission="material_receipts.delete">
                              <button
                                className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Excluir"
                                onClick={() => setDeleteId(receipt.material_receipt_id)}
                              >
                                <Trash2 size={16} />
                              </button>
                            </PermissionGuard>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Paginação — sempre visível quando há resultado: é por ela que se chega aos
          recebimentos mais antigos (a lista vem da data mais recente para a mais antiga). */}
      {totalReceipts > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-slate-500">
            Página {currentPage} de {totalPages} ({totalReceipts} registros)
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(1)}>
              Primeira
            </Button>
            <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>
              Anterior
            </Button>
            <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>
              Próxima
            </Button>
            <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}>
              Última
            </Button>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Excluir Recebimento de Material"
        message="Tem certeza que deseja excluir este recebimento? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        severity="danger"
        loading={deleting}
      />

      {/* Confirm receipt */}
      <ConfirmDialog
        isOpen={confirmId !== null}
        onClose={() => setConfirmId(null)}
        onConfirm={handleConfirmRow}
        title="Confirmar Recebimento"
        message={`Deseja confirmar o recebimento #${confirmId}? Esta ação irá processar a entrada no estoque e não poderá ser desfeita.`}
        confirmText="Confirmar"
        cancelText="Cancelar"
        severity="warning"
        loading={confirmingRow}
      />
    </div>
  );
};
